import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  subtitlesApi,
  type BulkTarget,
  type WantedSubtitle,
} from "@/api/subtitles";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { PageHeader } from "@/components/ui/PageHeader";
import { Skeleton } from "@/components/ui/Skeleton";
import { SearchInput } from "@/components/ui/Field";
import { Status } from "@/components/ui/Status";
import { useToast } from "@/components/ui/Toast";
import { cn } from "@/lib/cn";

/**
 * Wanted (Subtitles) page (issue #81).
 *
 * Surfaces every (media, language) gap from the API and lets the user
 * fire searches in two modes:
 *   - per-row "Search" — runs the provider chain against a single
 *     (media, language) pair. The row flips through optimistic states
 *     while the request is in flight.
 *   - "Search all" — bulk-runs the chain across the whole filtered
 *     list. Concurrency is bounded server-side at 3 workers.
 *
 * State machine for a row:
 *   idle → searching → found / not-found / error
 *
 * The optimistic states only live in the React tree; the next refresh
 * of the wanted list pulls authoritative data from the server.
 */

type RowState =
  | { kind: "idle" }
  | { kind: "searching" }
  | { kind: "found"; provider?: string }
  | { kind: "not-found" }
  | { kind: "error"; reason: string };

/** Stable composite key — `media_kind` + `media_id` + `language` */
function rowKey(g: { media_kind: string; media_id: number; language: string }) {
  return `${g.media_kind}:${g.media_id}:${g.language}`;
}

export function SubtitlesWanted() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [searchingAll, setSearchingAll] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number }>({
    done: 0,
    total: 0,
  });
  const [filter, setFilter] = useState("");
  const [rowStates, setRowStates] = useState<Record<string, RowState>>({});

  const { data: gaps, isLoading } = useQuery({
    queryKey: ["subtitles", "wanted"],
    queryFn: () => subtitlesApi.wanted(1000),
  });

  // Provider gate: searching with zero enabled providers always returns
  // "no match" and burns rate-limit capacity at the empty-search level.
  // We block both per-row and bulk search in that state and surface a
  // banner pointing to the settings page (issue: user feedback on
  // /subtitles/wanted with no providers configured).
  const { data: providers, isLoading: isLoadingProviders } = useQuery({
    queryKey: ["subtitles", "providers"],
    queryFn: subtitlesApi.providers.list,
  });
  const hasEnabledProvider = (providers ?? []).some((p) => p.enabled);
  const providersResolved = !isLoadingProviders;
  const searchBlocked = providersResolved && !hasEnabledProvider;

  const searchMut = useMutation({
    mutationFn: (g: WantedSubtitle) =>
      subtitlesApi.search({
        media_kind: g.media_kind,
        media_id: g.media_id,
        languages: [g.language],
      }),
  });

  const lower = filter.trim().toLowerCase();
  const filtered = useMemo<WantedSubtitle[]>(() => {
    if (!gaps) return [];
    if (!lower) return gaps;
    return gaps.filter(
      (g) =>
        g.title.toLowerCase().includes(lower) ||
        g.language.toLowerCase().includes(lower),
    );
  }, [gaps, lower]);

  const onSearch = (g: WantedSubtitle) => {
    if (searchBlocked) {
      toast(
        "No subtitle provider configured — visit Settings → Subtitles → Providers.",
        "error",
      );
      return;
    }
    const key = rowKey(g);
    setRowStates((s) => ({ ...s, [key]: { kind: "searching" } }));
    searchMut.mutate(g, {
      onSuccess: (resp) => {
        const lang = resp.languages.find(
          (l) => l.language.toLowerCase() === g.language.toLowerCase(),
        );
        if (resp.found > 0 && lang?.status === "found") {
          setRowStates((s) => ({
            ...s,
            [key]: {
              kind: "found",
              provider: lang.provider ?? undefined,
            },
          }));
          toast(
            `Found ${g.language.toUpperCase()} for ${g.title}` +
              (lang.provider ? ` via ${lang.provider}` : ""),
            "success",
          );
        } else if (lang?.status === "error") {
          const reason = lang.error ?? "search error";
          setRowStates((s) => ({ ...s, [key]: { kind: "error", reason } }));
          toast(reason, "error");
        } else {
          setRowStates((s) => ({ ...s, [key]: { kind: "not-found" } }));
        }
        qc.invalidateQueries({ queryKey: ["subtitles", "wanted"] });
      },
      onError: (err) => {
        const reason = err instanceof Error ? err.message : "Search failed";
        setRowStates((s) => ({ ...s, [key]: { kind: "error", reason } }));
        toast(reason, "error");
      },
    });
  };

  const searchAll = async () => {
    if (!filtered || filtered.length === 0) return;
    if (searchBlocked) {
      toast(
        "No subtitle provider configured — visit Settings → Subtitles → Providers.",
        "error",
      );
      return;
    }
    if (
      !confirm(
        `Search ${filtered.length} subtitle gap${
          filtered.length === 1 ? "" : "s"
        } across your providers? This may take several minutes.`,
      )
    ) {
      return;
    }
    setSearchingAll(true);
    setProgress({ done: 0, total: filtered.length });

    // Mark every row as searching so the table reflects in-progress
    // status while the bulk request runs server-side.
    setRowStates((prev) => {
      const next = { ...prev };
      for (const g of filtered) next[rowKey(g)] = { kind: "searching" };
      return next;
    });

    // Group by (media_kind, media_id) so we send one entry per media
    // with the union of languages — server runs the provider chain
    // once per media instead of N times.
    const grouped = new Map<string, BulkTarget>();
    for (const g of filtered) {
      const k = `${g.media_kind}:${g.media_id}`;
      const existing = grouped.get(k);
      if (existing) {
        existing.languages.push(g.language);
      } else {
        grouped.set(k, {
          media_kind: g.media_kind,
          media_id: g.media_id,
          languages: [g.language],
        });
      }
    }

    try {
      const resp = await subtitlesApi.bulkSearch({
        targets: Array.from(grouped.values()),
      });
      // Replay results into per-row state.
      setRowStates((prev) => {
        const next = { ...prev };
        for (const r of resp.results) {
          const key = `${r.media_kind}:${r.media_id}:${r.language}`;
          if (r.status === "found") {
            next[key] = { kind: "found", provider: r.provider ?? undefined };
          } else if (r.status === "error") {
            next[key] = { kind: "error", reason: r.error ?? "error" };
          } else {
            next[key] = { kind: "not-found" };
          }
        }
        return next;
      });
      setProgress({ done: resp.attempted, total: resp.attempted });
      toast(
        `Searched ${resp.attempted} gap${
          resp.attempted === 1 ? "" : "s"
        } — ${resp.found} found, ${resp.attempted - resp.found} no match / failed`,
        resp.found > 0 ? "success" : "info",
      );
    } catch (err) {
      const reason = err instanceof Error ? err.message : "Bulk search failed";
      toast(reason, "error");
      // Bail out of optimistic "searching" — fall back to idle so the
      // row is interactable again.
      setRowStates((prev) => {
        const next = { ...prev };
        for (const k of Object.keys(next)) {
          if (next[k].kind === "searching") next[k] = { kind: "idle" };
        }
        return next;
      });
    } finally {
      setSearchingAll(false);
      qc.invalidateQueries({ queryKey: ["subtitles", "wanted"] });
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Wanted"
        title="Subtitle gaps"
        description="Required-language subtitles that haven't been downloaded yet. Search per row, or batch-search the whole list."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                qc.invalidateQueries({ queryKey: ["subtitles", "wanted"] })
              }
              aria-label="Refresh"
              title="Refresh"
            >
              <svg
                className="h-4 w-4"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
                aria-hidden
              >
                <path
                  d="M2 8a6 6 0 0 1 11-3.3M14 8a6 6 0 0 1-11 3.3"
                  strokeLinecap="round"
                />
                <path d="M13 1.5V5h-3.5M3 14.5V11h3.5" strokeLinecap="round" />
              </svg>
            </Button>
            <Button
              onClick={searchAll}
              loading={searchingAll}
              disabled={!filtered || filtered.length === 0 || searchBlocked}
              title={
                searchBlocked
                  ? "No subtitle provider configured"
                  : undefined
              }
            >
              {searchingAll
                ? `Searching ${progress.done}/${progress.total}`
                : "Search all"}
            </Button>
          </div>
        }
      />

      {searchBlocked && <NoProviderBanner />}

      <div className="flex items-center justify-between gap-4">
        <div className="w-full max-w-sm">
          <SearchInput
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by title or language"
          />
        </div>
        {gaps && (
          <span className="font-mono text-xs text-foreground/55">
            {filter
              ? `${filtered.length} of ${gaps.length} gaps`
              : `${gaps.length} gap${gaps.length === 1 ? "" : "s"}`}
          </span>
        )}
      </div>

      {isLoading ? (
        <Skeleton.Rows count={8} />
      ) : !gaps || gaps.length === 0 ? (
        <EmptyState
          glyph="search"
          title="No subtitle gaps"
          description="Every required-language subtitle is in place. Add languages to a profile or import more media."
          action={
            <Link to="/settings/subtitles">
              <Button variant="secondary">Subtitle settings</Button>
            </Link>
          }
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          glyph="search"
          title="No matches"
          description={`Nothing matches "${filter}".`}
          action={
            <Button variant="ghost" onClick={() => setFilter("")}>
              Clear filter
            </Button>
          }
        />
      ) : (
        <div className="overflow-x-auto rounded-md border border-line bg-surface/30">
          <table className="w-full text-sm">
            <thead className="bg-surface-2/50">
              <tr className="text-left">
                <th className="w-16 px-3 py-2.5"></th>
                <th className="px-3 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                  Title
                </th>
                <th className="px-3 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                  Language
                </th>
                <th className="px-3 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                  Required since
                </th>
                <th className="px-3 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                  Last attempt
                </th>
                <th className="px-3 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                  Status
                </th>
                <th className="w-32 px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line/60">
              {filtered.map((g) => (
                <WantedRow
                  key={rowKey(g)}
                  gap={g}
                  state={rowStates[rowKey(g)] ?? { kind: "idle" }}
                  onSearch={() => onSearch(g)}
                  searchBlocked={searchBlocked}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function WantedRow({
  gap,
  state,
  onSearch,
  searchBlocked,
}: {
  gap: WantedSubtitle;
  state: RowState;
  onSearch: () => void;
  searchBlocked: boolean;
}) {
  const dim = state.kind === "found";
  return (
    <tr
      className={cn(
        "group transition hover:bg-surface-2/40",
        dim && "opacity-60",
      )}
    >
      <td className="px-3 py-2">
        {gap.poster_url ? (
          <img
            src={gap.poster_url}
            alt=""
            className="aspect-[2/3] w-12 rounded border border-line object-cover"
            loading="lazy"
          />
        ) : (
          <div className="aspect-[2/3] w-12 rounded border border-line bg-surface-2/40" />
        )}
      </td>
      <td className="px-3 py-2.5">
        <span className="font-display text-base font-medium tracking-display-tight">
          {gap.title}
        </span>
        <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.12em] text-foreground/45">
          {gap.media_kind}
        </div>
      </td>
      <td className="px-3 py-2.5 font-mono text-xs uppercase tabular-nums text-foreground/75">
        {gap.language}
      </td>
      <td className="px-3 py-2.5 font-mono text-xs text-foreground/65">
        {formatRelative(gap.required_since)}
      </td>
      <td className="px-3 py-2.5 font-mono text-xs text-foreground/65">
        {gap.last_attempt_at ? (
          <>
            <div>{formatRelative(gap.last_attempt_at)}</div>
            {gap.last_attempt_status && (
              <div className="text-foreground/45">
                {gap.last_attempt_status}
              </div>
            )}
          </>
        ) : (
          "—"
        )}
      </td>
      <td className="px-3 py-2.5">
        <RowStatus state={state} />
      </td>
      <td className="space-x-2 px-3 py-2 text-right whitespace-nowrap">
        {state.kind === "found" ? (
          <span className="font-mono text-[10px] text-foreground/45">
            {state.provider ?? "ok"}
          </span>
        ) : (
          <Button
            size="sm"
            variant="secondary"
            loading={state.kind === "searching"}
            disabled={searchBlocked}
            title={
              searchBlocked
                ? "No subtitle provider configured"
                : undefined
            }
            onClick={onSearch}
          >
            {state.kind === "error" || state.kind === "not-found"
              ? "Retry"
              : "Search"}
          </Button>
        )}
      </td>
    </tr>
  );
}

function RowStatus({ state }: { state: RowState }) {
  switch (state.kind) {
    case "idle":
      return (
        <Status variant="chip" tone="accent">
          wanted
        </Status>
      );
    case "searching":
      return (
        <Status variant="chip" tone="info" pulse>
          searching
        </Status>
      );
    case "found":
      return (
        <Status variant="chip" tone="good">
          found
        </Status>
      );
    case "not-found":
      return (
        <Status variant="chip" tone="neutral">
          no match
        </Status>
      );
    case "error":
      return (
        <Status variant="chip" tone="bad">
          {state.reason.length > 22 ? "error" : state.reason}
        </Status>
      );
  }
}

/**
 * Inline warning surfaced when the user lands on the Wanted page with
 * zero enabled subtitle providers. Without one, every search call goes
 * out to the empty-set provider chain and immediately returns "no
 * match" — confusing on its own and worse, it stamps `last_attempt_at`
 * so the row looks like it was searched. We block search entirely and
 * point the user at the settings page where they can add one.
 */
function NoProviderBanner() {
  return (
    <div
      role="alert"
      className={cn(
        "rounded-md border border-warn/40 bg-warn/[0.06] px-4 py-3",
        "flex flex-wrap items-center justify-between gap-3 text-sm",
      )}
    >
      <div>
        <div className="font-display text-base font-medium text-warn">
          No subtitle provider configured
        </div>
        <div className="mt-0.5 text-foreground/70">
          Searching has been disabled. Add and enable at least one provider
          to look up subtitles for these gaps.
        </div>
      </div>
      <Link to="/settings/subtitles">
        <Button variant="secondary" size="sm">
          Configure providers
        </Button>
      </Link>
    </div>
  );
}

/** Compact relative timestamp: "3d ago", "2h ago" — falls back to ISO
 *  date for anything older than a week so the "Required since" column
 *  doesn't lie when a gap has been around for months. */
function formatRelative(iso: string | null | undefined): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const diffMs = Date.now() - t;
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d}d ago`;
  return iso.slice(0, 10);
}
